import { useRef, useState, useCallback } from "react";
import type { KeyboardEvent } from "react";
import { X, Search } from "lucide-react";
import { CommandGroup, CommandItem, CommandList, Command } from "cmdk";
import { Badge } from "@/components/shadcn/badge";
import { PlayerStats, GoalkeeperStats } from "@/api/ranking";

type ComparisonPlayerStats = PlayerStats | GoalkeeperStats;

interface ComparisonEngineMultiSelectProps<T extends ComparisonPlayerStats | PlayerStats> {
    data: T[];
    selected: T[];
    setSelected: (players: T[] | ((prev: T[]) => T[])) => void;
}

export function ComparisonEngineMultiSelect<T extends ComparisonPlayerStats | PlayerStats>({
    data,
    selected,
    setSelected,
}: ComparisonEngineMultiSelectProps<T>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const handleUnselect = useCallback(
        (player: T) => {
            setSelected((prev) => prev.filter((s) => s.playerId !== player.playerId));
        },
        [setSelected]
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            const input = inputRef.current;
            if (!input) return;

            if ((e.key === "Delete" || e.key === "Backspace") && input.value === "") {
                setSelected((prev) => {
                    const copy = [...prev];
                    copy.pop();
                    return copy;
                });
                input.blur();
                setOpen(false);
            }

            if (e.key === "Escape") {
                input.blur();
                setOpen(false);
            }
        },
        [setSelected]
    );

    const canSelectMore = selected.length < 2;
    const getPlayerName = (p: T) => `${p.firstName} ${p.lastName}`;
    const getPlaceholder = () => (selected.length === 0 ? "Select up to 2 players" : "Add another player");

    const getRoleColor = (role?: string) => {
        const roleColors = {
            RW: "text-red-accent",
            LW: "text-red-accent",
            RD: "text-green-accent",
            LD: "text-green-accent",
            Mixed: "text-yellow-accent",
            GK: "text-blue-500",
        } as const;
        return role && role in roleColors ? roleColors[role as keyof typeof roleColors] : "";
    };

    const isGoalkeeper = (player: T) => player.role === "GK";
    const firstPlayerIsGoalkeeper = selected.length > 0 && isGoalkeeper(selected[0]);
    const firstPlayerIsNotGoalkeeper = selected.length > 0 && !isGoalkeeper(selected[0]);

    const filteredData = firstPlayerIsGoalkeeper
        ? data.filter((player) => isGoalkeeper(player))
        : firstPlayerIsNotGoalkeeper
          ? data.filter((player) => !isGoalkeeper(player))
          : data;

    return (
        <Command onKeyDown={handleKeyDown} className="overflow-visible bg-transparent">
            <div className="group rounded-md border px-3 py-2 text-sm ring-offset-background border-table-border bg-white">
                <div className="flex flex-wrap items-center gap-2">
                    <Search className="pointer-events-none h-4 w-4 text-on-surface-variant shrink-0" />
                    {selected.map((player) => (
                        <Badge key={getPlayerName(player)} variant="secondary">
                            {getPlayerName(player)}
                            {(player as T & { teamName?: string }).teamName && (
                                <span className="text-[11px] text-on-surface-variant ml-1">
                                    ({(player as T & { teamName?: string }).teamName})
                                </span>
                            )}
                            <button
                                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleUnselect(player);
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={() => handleUnselect(player)}
                            >
                                <X className="h-6 w-3 text-muted-foreground hover:text-foreground" />
                            </button>
                        </Badge>
                    ))}
                    {canSelectMore && (
                        <Command.Input
                            ref={inputRef}
                            value={inputValue}
                            onValueChange={setInputValue}
                            onBlur={() => setOpen(false)}
                            onFocus={() => setOpen(true)}
                            placeholder={getPlaceholder()}
                            className="flex-1 min-w-[120px] text-on-surface bg-transparent outline-none placeholder:text-on-surface-variant"
                        />
                    )}
                </div>
            </div>
            <div className="relative mt-1">
                <CommandList>
                    {open && canSelectMore && filteredData.length > 0 ? (
                        <div className="absolute top-0 z-50 w-full rounded-md border border-table-border bg-white text-popover-foreground shadow-md outline-none animate-in">
                            <CommandGroup className="max-h-40 overflow-auto">
                                {filteredData.map((player) => {
                                    const isAlreadySelected = selected.some((s) => s.playerId === player.playerId);

                                    const handleSelect = () => {
                                        if (isAlreadySelected) return;

                                        setInputValue("");
                                        setSelected((prev) => [...prev, player]);

                                        const willReachLimit = selected.length + 1 >= 2;
                                        if (willReachLimit) {
                                            inputRef.current?.blur();
                                            setOpen(false);
                                        } else {
                                            inputRef.current?.focus();
                                            setOpen(true);
                                        }
                                    };

                                    return (
                                        <CommandItem
                                            key={getPlayerName(player)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onSelect={handleSelect}
                                            className={`text-sm py-2 px-3 ${
                                                isAlreadySelected
                                                    ? "opacity-50 pointer-events-none text-on-surface-variant"
                                                    : "cursor-pointer text-on-surface hover:bg-surface-container"
                                            }`}
                                        >
                                            {getPlayerName(player)}
                                            <span className="text-sm text-on-surface-variant ml-1">
                                                {player.playerId}
                                            </span>
                                            <span className={`ml-2 text-xs font-semibold ${getRoleColor(player.role)}`}>
                                                {player.role}
                                            </span>
                                            {(player as T & { teamName?: string }).teamName && (
                                                <span className="ml-auto text-xs text-on-surface-variant">
                                                    {(player as T & { teamName?: string }).teamName}
                                                </span>
                                            )}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </div>
                    ) : null}
                </CommandList>
            </div>
        </Command>
    );
}
